import { CheckCircleFilled, ChevronRight, RocketLaunchFilled } from "@clockwise/icons";
import classNames from "classnames";
import React, { useState } from "react";
import { NUXPreferencesModal } from "../nux-education-modals/NUXPreferencesModal";
import { NUXReschedulingModal } from "../nux-education-modals/NUXReschedulingModal";
import { NUXSchedulingModal } from "../nux-education-modals/NUXSchedulingModal";
import { useChecklistFlags } from "./useChecklistFlags";

export const ChecklistItem = ({
  number,
  onClick,
  title,
  subtext,
  disabled,
}: {
  number: number;
  onClick: () => void;
  title: string;
  subtext: string;
  disabled?: boolean;
}) => {
  const [isHovering, setIsHovering] = useState(false);

  const handleClick = () => {
    if (!disabled) {
      onClick();
    }
  };

  const handleMouseEnter = () => {
    if (!disabled) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (!disabled) {
      setIsHovering(false);
    }
  };

  return (
    <div
      className={classNames("cw-flex cw-gap-x-3 cw-items-center cw-my-2", {
        "cw-cursor-pointer": !disabled,
      })}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {disabled ? (
        <div className="cw-flex cw-items-center cw-justify-center cw-rounded-[100%] cw-h-4 cw-w-4 cw-basis-[16px] cw-border cw-border-solid cw-border-muted">
          <CheckCircleFilled className="cw-text-subtle cw-w-4 cw-h-4" />
        </div>
      ) : (
        <div className="cw-flex cw-items-center cw-justify-center cw-text-[8px] cw-font-semibold cw-text-onEmphasis cw-bg-positive-emphasis cw-rounded-[100%] cw-h-4 cw-w-[14px] cw-basis-[16px] cw-border cw-border-solid cw-border-brand-interactable">
          {number}
        </div>
      )}
      <div
        className={classNames("cw-flex cw-flex-col cw-w-full cw-gap-y-0.5", {
          "cw-opacity-60": disabled,
        })}
      >
        <div
          className={classNames(
            "cw-text-13 cw-font-medium cw-text-default cw-flex cw-items-center cw-h-4",
            {
              "cw-text-positive": isHovering,
            },
          )}
        >
          {title}
          {isHovering && <ChevronRight className="cw-text-positive cw-ml-1 cw-w-4 cw-h-4" />}
        </div>
        <div className="cw-text-13 cw-text-subtle"> {subtext}</div>
      </div>
    </div>
  );
};

enum ModalType {
  Scheduling = "Scheduling",
  Rescheduling = "Rescheduling",
  Preferences = "Preferences",
}
export const SuperpowersChecklist = () => {
  const [openModal, setOpenModal] = useState<ModalType | null>(null);
  const {
    schedulingCompleted,
    reschedulingCompleted,
    preferencesCompleted,
    completeSchedulingChecklistItem,
    completeReschedulingChecklistItem,
    completePreferencesChecklistItem,
  } = useChecklistFlags();

  const onClickScheduling = () => {
    setOpenModal(ModalType.Scheduling);
    completeSchedulingChecklistItem();
  };

  const onClickQuickRescheduling = () => {
    setOpenModal(ModalType.Rescheduling);
    completeReschedulingChecklistItem();
  };

  const onClickCalendarPreferences = () => {
    setOpenModal(ModalType.Preferences);
    completePreferencesChecklistItem();
  };

  const percentageCompleted = Math.round(
    ([schedulingCompleted, reschedulingCompleted, preferencesCompleted].filter(
      (completed) => completed === true,
    ).length /
      3) *
      100,
  );

  const getPercentageCompleteEmoji = (percent: number) => {
    if (percent === 33) {
      return " 🙂";
    } else if (percent === 67) {
      return "☺️";
    } else if (percent === 100) {
      return "🥳";
    } else {
      return "😢";
    }
  };

  return (
    <div className="cw-flex cw-flex-col cw-gapy-y-2">
      <div className="cw-flex cw-justify-between cw-items-center cw-gap-y-3">
        <RocketLaunchFilled className="cw-text-subtle cw-w-4 cw-h-4 cw-mr-2" />
        <div className="cw-text-subtle cw-font-medium cw-text-13"> Clockwise Superpowers</div>
        <div className="cw-border-t cw-border-solid cw-border-subtle cw-mx-2 cw-grow" />
        <div className="cw-flex cw-px-2 cw-py-0.5 cw-rounded-xl cw-items-center cw-bg-neutral-inset cw-font-medium cw-text-[12px]">
          {`${percentageCompleted}%  ${getPercentageCompleteEmoji(percentageCompleted)}`}
        </div>
      </div>
      <ChecklistItem
        number={1}
        onClick={onClickScheduling}
        title="Powerful scheduling"
        subtext="Find availability in an instant"
        disabled={schedulingCompleted}
      />

      <ChecklistItem
        number={2}
        onClick={onClickQuickRescheduling}
        title="Quick rescheduling"
        subtext="Get smart suggestions in one click"
        disabled={reschedulingCompleted}
      />
      <ChecklistItem
        number={3}
        onClick={onClickCalendarPreferences}
        title="Calendar preferences"
        subtext="Craft a schedule that works for you"
        disabled={preferencesCompleted}
      />

      {openModal === ModalType.Scheduling && (
        <NUXSchedulingModal
          onClose={() => setOpenModal(null)}
          onClickAction={() => setOpenModal(null)}
        />
      )}
      {openModal === ModalType.Rescheduling && (
        <NUXReschedulingModal
          onClose={() => setOpenModal(null)}
          onClickAction={() => setOpenModal(null)}
        />
      )}
      {openModal === ModalType.Preferences && (
        <NUXPreferencesModal
          onClose={() => setOpenModal(null)}
          onClickAction={() => setOpenModal(null)}
        />
      )}
    </div>
  );
};
